import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { SearchableSelect } from '../SearchableSelect'
import { apiClient } from '../../api/client'
import type { components } from '../../api/schema'
import { parseApiError } from '../../lib/apiError'
import { compactLocalized, getLocalizedName } from '../../lib/localization'

type AssetType = components['schemas']['AssetType']
type CreateAssetTypeRequest = components['schemas']['CreateAssetTypeRequest']

const NO_PARENT = '__none__'
const CODE_PATTERN = /^[a-z0-9_]{3,}$/

/** The API serves `name` as a locale → label map, despite the schema's string. */
export function assetTypeLabel(at: Pick<AssetType, 'name' | 'code'>, locale: string): string {
  const name = at.name as unknown
  if (name && typeof name === 'object') return getLocalizedName(name as Record<string, string>, locale) || at.code
  return (name as string) || at.code
}

/** Derives a type code (`^[a-z0-9_]{3,}$`) from a free-text name. */
export function codeFromName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

interface CreateAssetTypeDialogProps {
  org: string
  open: boolean
  onOpenChange: (open: boolean) => void
  assetTypes: AssetType[]
}

/**
 * Declares a new asset type, then opens its page. The API takes the
 * inheritance link on PATCH only, so a chosen parent is set right after the
 * create.
 */
export function CreateAssetTypeDialog({ org, open, onOpenChange, assetTypes }: CreateAssetTypeDialogProps) {
  const { t, i18n } = useTranslation('types')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [names, setNames] = useState({ en: '', fr: '' })
  const [code, setCode] = useState('')
  const [codeTouched, setCodeTouched] = useState(false)
  const [inheritsFrom, setInheritsFrom] = useState('')
  const [error, setError] = useState<string | null>(null)

  const effectiveCode = codeTouched ? code : codeFromName(names.en || names.fr)
  const codeValid = CODE_PATTERN.test(effectiveCode)
  const hasName = Boolean(names.en.trim() || names.fr.trim())

  const parentOptions = useMemo(
    () => [
      { value: NO_PARENT, label: t('asset_types.create_dialog.no_inherits_from') },
      ...assetTypes
        .map(at => ({
          value: at.uid,
          label: `${assetTypeLabel(at, i18n.language)} (${at.code})`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [assetTypes, i18n.language, t]
  )

  const reset = () => {
    setNames({ en: '', fr: '' })
    setCode('')
    setCodeTouched(false)
    setInheritsFrom('')
    setError(null)
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      const created = await apiClient.POST('/api/v1/organizations/{orgCode}/asset-types', {
        params: { path: { orgCode: org } },
        // The spec types `name` as a bare object; it is a locale → label map.
        body: { code: effectiveCode, name: compactLocalized(names) as CreateAssetTypeRequest['name'] },
      })
      // The spec declares no error response here, so `error` is typed never.
      if (!created.data) throw (created as { error?: unknown }).error ?? new Error('create failed')
      if (inheritsFrom) {
        const patched = await apiClient.PATCH('/api/v1/organizations/{orgCode}/asset-types/{assetTypeUid}', {
          params: { path: { orgCode: org, assetTypeUid: created.data.uid } },
          body: { inherits_from_asset_type_uid: inheritsFrom },
        })
        if (patched.error) throw patched.error
      }
      return created.data
    },
    onSuccess: created => {
      queryClient.invalidateQueries({ queryKey: ['get', '/api/v1/organizations/{orgCode}/asset-types'] })
      onOpenChange(false)
      reset()
      navigate({
        to: '/$org/asset-types/$assetTypeUid',
        params: { org, assetTypeUid: created.uid },
        search: { tab: 'overview' },
      })
    },
    onError: err => {
      const payload = parseApiError(err)
      setError(payload?.fields?.[0]?.message || payload?.title || t('asset_types.create_dialog.error'))
    },
  })

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) reset()
        onOpenChange(next)
      }}
    >
      <DialogContent data-testid="create-asset-type-dialog">
        <DialogHeader>
          <DialogTitle>{t('asset_types.create_dialog.title')}</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-4 p-6 pt-2"
          onSubmit={e => {
            e.preventDefault()
            if (hasName && codeValid && !createMutation.isPending) createMutation.mutate()
          }}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="new-asset-type-name-en">🇺🇸 {t('asset_types.create_dialog.name_en_label')}</Label>
              <Input
                id="new-asset-type-name-en"
                value={names.en}
                onChange={e => setNames(prev => ({ ...prev, en: e.target.value }))}
                autoFocus
                data-testid="create-asset-type-name-en"
              />
            </div>
            <div>
              <Label htmlFor="new-asset-type-name-fr">🇫🇷 {t('asset_types.create_dialog.name_fr_label')}</Label>
              <Input
                id="new-asset-type-name-fr"
                value={names.fr}
                onChange={e => setNames(prev => ({ ...prev, fr: e.target.value }))}
                data-testid="create-asset-type-name-fr"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="new-asset-type-code">{t('asset_types.create_dialog.code_label')}</Label>
            <Input
              id="new-asset-type-code"
              value={effectiveCode}
              onChange={e => {
                setCodeTouched(true)
                setCode(e.target.value)
              }}
              className="font-mono"
              data-testid="create-asset-type-code"
            />
            <p className="mt-1 text-xs text-muted-foreground">{t('asset_types.create_dialog.code_hint')}</p>
          </div>

          <div>
            <Label>{t('asset_types.create_dialog.inherits_from_label')}</Label>
            <SearchableSelect
              options={parentOptions}
              value={inheritsFrom || NO_PARENT}
              onValueChange={value => setInheritsFrom(value === NO_PARENT ? '' : value)}
              placeholder={t('asset_types.create_dialog.no_inherits_from')}
              searchPlaceholder={t('asset_types.search_placeholder')}
              emptyMessage={t('asset_types.no_results')}
              data-testid="create-asset-type-inherits-from"
            />
            <p className="mt-1 text-xs text-muted-foreground">{t('asset_types.create_dialog.inherits_from_hint')}</p>
          </div>

          {error && (
            <p className="text-sm text-destructive" data-testid="create-asset-type-error">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset()
                onOpenChange(false)
              }}
              disabled={createMutation.isPending}
              data-testid="create-asset-type-cancel"
            >
              {t('asset_types.create_dialog.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending || !hasName || !codeValid}
              data-testid="create-asset-type-submit"
            >
              {createMutation.isPending ? t('asset_types.create_dialog.creating') : t('asset_types.create_dialog.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
